import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import {
  usePatchAhuPositionMutation,
} from 'app/services/ahuBoardFormsApi';
import { openMountingPopup } from 'app/slices/ahuBoardSlice';
import type { IAhuPositionRow } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { validateAccessoryOnChange } from '../validation/validationController';

/**
 * Phase 6e1 — mounting popup HOST (§13 row 10 — consumes the 6d
 * popupSeams.mountingPopupAccessoryId seam; canvas dblclick on HOOD/DAMPER/
 * LOUVER/OPENING/ACCESS_DOOR, error rows and the 1.5 s-after-create flow all
 * write it).
 *
 * 6e1 scope: the on-cabinet plane/X/Y core (position PATCH → §19.2 position
 * cascade: validateAccessoryOnChange + canvas re-draw via accessory-form
 * channels). The full §51.4 mounting engine (special-parameters templates,
 * int-wall planes, drag-on-plane) ships with the accessory FORMS (6e2) —
 * documented deferral.
 */

const PLANES: Array<{ id: number; name: string }> = [
  { id: 1, name: 'Floor' },
  { id: 2, name: 'Roof' },
  { id: 3, name: 'Upstream Wall' },
  { id: 4, name: 'Downstream Wall' },
  { id: 5, name: 'Left Wall' },
  { id: 6, name: 'Right Wall' },
  { id: 9, name: 'None' },
];

const MountingPopup = () => {
  const dispatch = useAppDispatch();
  const accessoryId = useAppSelector((s) => s.ahuBoard.popupSeams.mountingPopupAccessoryId);
  const [position, setPosition] = useState<IAhuPositionRow | null>(null);
  const [patchPosition] = usePatchAhuPositionMutation();

  useEffect(() => {
    if (accessoryId == null) {
      setPosition(null);
      return undefined;
    }
    let cancelled = false;
    const sub = dispatch(
      ahuBoardApi.endpoints.getAccessoryPosition.initiate(accessoryId, {
        forceRefetch: true,
      })
    );
    sub
      .unwrap()
      .then((pos) => {
        if (!cancelled) setPosition(pos);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [accessoryId, dispatch]);

  if (accessoryId == null || !position) return null;

  const close = () => dispatch(openMountingPopup(null));

  const save = (data: Record<string, unknown>) => {
    patchPosition({ id: Number(position.id), data })
      .unwrap()
      .then((saved) => {
        setPosition((prev) => (prev ? { ...prev, ...saved } : prev));
        // §19.2 position cascade.
        void dispatch(validateAccessoryOnChange(accessoryId));
        ahuBoardBus.emit('redraw:canvas');
      })
      .catch(() => undefined);
  };

  return (
    <div className="popup-wrapper tunnel-popup location-popup active mounting-popup">
      <div className="popup-content popup-content-animation">
        <h2 className="popup-heading">Mounting</h2>
        <div className="popup-content-part">
          <ul className="sublist">
            <li>
              <div className="single-line">
                <select
                  name="mounting_plane_id"
                  className="position_field"
                  value={position.mounting_plane_id ?? ''}
                  onChange={(e) => save({ mounting_plane_id: Number(e.currentTarget.value) })}
                >
                  {PLANES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <span className="label">Mounting Plane</span>
              </div>
            </li>
            <li>
              <div>
                <span className="unit">in</span>
                <input
                  type="text"
                  data-type="number"
                  data-step="0.01"
                  name="x"
                  className="position_field"
                  defaultValue={String(position.x ?? '')}
                  onBlur={(e) => {
                    const v = Number(e.currentTarget.value);
                    if (Number.isFinite(v)) save({ x: v, default_pos_x: false });
                  }}
                />
                <span className="label">X</span>
              </div>
            </li>
            <li>
              <div>
                <span className="unit">in</span>
                <input
                  type="text"
                  data-type="number"
                  data-step="0.01"
                  name="y"
                  className="position_field"
                  defaultValue={String(position.y ?? '')}
                  onBlur={(e) => {
                    const v = Number(e.currentTarget.value);
                    if (Number.isFinite(v)) save({ y: v, default_pos_y: false });
                  }}
                />
                <span className="label">Y</span>
              </div>
            </li>
          </ul>
        </div>
        <div className="popup-control-buttons">
          <button className="btn cancel" onClick={close}>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

export default MountingPopup;
